import { Vector3 } from '../../types/Vector3'
import { Quaternion } from '../../types/Quaternion'
import { LayoutInterface, AlignmentMode } from '../FieldTypes'
import { FieldParameters } from '../FieldParameters'
import { CurveUtils } from '../../../math/CurveUtils'
import { VectorUtils } from '../../../math/VectorUtils'

export class SpiralLayout implements LayoutInterface {
  // ==================== POSITION CALCULATION ====================
  
  /**
   * Calculate position for object at given index
   */
  public computePosition(index: number, totalCount: number, params: FieldParameters): Vector3 {
    if (totalCount <= 1) {
      return new Vector3()
    }
    
    // Calculate parameter t (0 to 1)
    const t = index / (totalCount - 1)
    
    // Calculate spacing if using object-based spacing
    const spacing = this.calculateSpacing(params, totalCount)
    
    // Determine if using tapered radius
    const useRadiusTaper = params.startRadius !== params.endRadius
    
    let position: Vector3
    
    if (useRadiusTaper) {
      // Use tapered spiral
      position = CurveUtils.getPointOnTaperedSpiral(
        new Vector3(), // Center at origin
        params.startRadius,
        params.endRadius,
        params.height,
        params.turns,
        t
      )
    } else {
      // Use regular spiral
      position = CurveUtils.getPointOnSpiral(
        new Vector3(), // Center at origin
        params.radius,
        params.height,
        params.turns,
        t
      )
    }
    
    // Apply spacing adjustment if needed
    if (params.useObjectSpacing) {
      position = this.adjustPositionForSpacing(position, t, spacing, params)
    }
    
    return position
  }
  
  // ==================== ORIENTATION CALCULATION ====================
  
  /**
   * Calculate orientation for object at given index using improved Frenet frame math
   */
  public computeOrientation(index: number, totalCount: number, params: FieldParameters): Quaternion {
    const t = index / (totalCount - 1)
    const center = new Vector3()
    
    // Determine spiral parameters
    const useRadiusTaper = params.startRadius !== params.endRadius
    const radius = useRadiusTaper 
      ? params.startRadius + (params.endRadius - params.startRadius) * t
      : params.radius
    
    // Get Frenet frame for the spiral
    const frenetFrame = CurveUtils.getSpiralFrenetFrame(
      center,
      radius,
      params.height,
      params.turns,
      t
    )
    
    // Determine up mode based on parameters
    const upMode = params.upVectorMode === 'world-up' ? 'world' : 'path'
    
    // Determine alignment mode
    const orientation = params.alignmentMode === AlignmentMode.Aligned ? 'aligned' : 'perpendicular'
    
    // Calculate orientation using the improved math
    const { right, up, forward } = CurveUtils.calculateOrientation(
      frenetFrame.tangent,
      frenetFrame.normal,
      frenetFrame.binormal,
      orientation,
      upMode
    )
    
    // Debug logging for first object only
    if (index === 0 && Math.floor(Date.now() / 5000) % 10 === 0) { // Log occasionally
      //console.log(`🔄 Spiral ${orientation.toUpperCase()} ${upMode.toUpperCase()}-UP:`)
      //console.log(`  t: ${t.toFixed(2)}, radius: ${radius.toFixed(2)}`)
      //console.log(`  Tangent: [${frenetFrame.tangent.x.toFixed(2)}, ${frenetFrame.tangent.y.toFixed(2)}, ${frenetFrame.tangent.z.toFixed(2)}]`)
      //console.log(`  Normal:  [${frenetFrame.normal.x.toFixed(2)}, ${frenetFrame.normal.y.toFixed(2)}, ${frenetFrame.normal.z.toFixed(2)}]`)
      //console.log(`  Forward: [${forward.x.toFixed(2)}, ${forward.y.toFixed(2)}, ${forward.z.toFixed(2)}]`)
      //console.log(`  Up:      [${up.x.toFixed(2)}, ${up.y.toFixed(2)}, ${up.z.toFixed(2)}]`)
    }
    
    // Create rotation matrix from the basis vectors
    return VectorUtils.lookRotation(forward, up)
  }
  
  // ==================== TANGENT CALCULATION ====================
  
  /**
   * Calculate tangent vector (direction along spiral)
   */
  public computeTangent(index: number, totalCount: number, params: FieldParameters): Vector3 {
    const t = index / (totalCount - 1)
    
    // Use tapered spiral if different start/end radius
    const useRadiusTaper = params.startRadius !== params.endRadius
    const radius = useRadiusTaper 
      ? params.startRadius + (params.endRadius - params.startRadius) * t
      : params.radius
    
    return CurveUtils.getSpiralTangent(
      radius,
      params.height,
      params.turns,
      t
    )
  }
  
  // ==================== NORMAL CALCULATION ====================
  
  /**
   * Calculate normal vector (radial direction)
   */
  public computeNormal(index: number, totalCount: number, params: FieldParameters): Vector3 {
    const position = this.computePosition(index, totalCount, params)
    const center = new Vector3(0, position.y, 0) // Center at same height
    return VectorUtils.getInwardNormal(position, center)
  }
  
  // ==================== UTILITY METHODS ====================
  
  /**
   * Calculate spacing between objects
   */
  private calculateSpacing(params: FieldParameters, totalCount: number): number {
    if (params.useObjectSpacing && params.objectBounds) {
      // Use object bounds for spacing
      const objectSize = params.objectBounds.size
      const maxSize = Math.max(objectSize.x, objectSize.y, objectSize.z)
      return maxSize * params.spacingMultiplier
    } else {
      // Use fixed cell size plus padding
      return params.cellSize + params.cellSpacing
    }
  }
  
  /**
   * Get main directional vector of the layout (spiral combined direction)
   */
  public getDirectionalVector(params: FieldParameters): Vector3 {
    // Use fieldDirection as the main spiral axis direction
    return params.fieldDirection.clone().normalize()
  }
  
  /**
   * Adjust position for object-based spacing
   */
  private adjustPositionForSpacing(
    position: Vector3,
    t: number,
    spacing: number,
    params: FieldParameters
  ): Vector3 {
    // This is a simplified adjustment - in practice, you'd need to
    // redistribute points along the spiral arc length
    return position
  }
  
  /**
   * Calculate path-up vector for spiral
   */
  private computePathUp(index: number, totalCount: number, params: FieldParameters): Vector3 {
    const tangent = this.computeTangent(index, totalCount, params)
    const normal = this.computeNormal(index, totalCount, params)
    return VectorUtils.getBinormal(tangent, normal)
  }
  
  // ==================== ADVANCED FEATURES ====================
  
  /**
   * Get spiral length (arc length)
   */
  public getSpiralLength(totalCount: number, params: FieldParameters): number {
    const spiralFunction = (t: number) => {
      const useRadiusTaper = params.startRadius !== params.endRadius
      
      if (useRadiusTaper) {
        return CurveUtils.getPointOnTaperedSpiral(
          new Vector3(),
          params.startRadius,
          params.endRadius,
          params.height,
          params.turns,
          t
        )
      } else {
        return CurveUtils.getPointOnSpiral(
          new Vector3(),
          params.radius,
          params.height,
          params.turns,
          t
        )
      }
    }
    
    return CurveUtils.calculateArcLength(spiralFunction, 0, 1, 100)
  }
  
  /**
   * Get point at specific parameter t
   */
  public getPointAtParameter(t: number, params: FieldParameters): Vector3 {
    const useRadiusTaper = params.startRadius !== params.endRadius
    
    if (useRadiusTaper) {
      return CurveUtils.getPointOnTaperedSpiral(
        new Vector3(),
        params.startRadius,
        params.endRadius,
        params.height,
        params.turns,
        t
      )
    } else {
      return CurveUtils.getPointOnSpiral(
        new Vector3(),
        params.radius,
        params.height,
        params.turns,
        t
      )
    }
  }
  
  /**
   * Find closest point on spiral
   */
  public findClosestPoint(targetPoint: Vector3, totalCount: number, params: FieldParameters): {
    point: Vector3
    index: number
    distance: number
    parameter: number
  } {
    let minDistance = Infinity
    let closestPoint = new Vector3()
    let closestIndex = 0
    let closestParameter = 0
    
    // Sample points along spiral to find closest
    const sampleCount = totalCount * 2 // Higher resolution
    
    for (let i = 0; i < sampleCount; i++) {
      const t = i / (sampleCount - 1)
      const point = this.getPointAtParameter(t, params)
      const distance = targetPoint.distanceTo(point)
      
      if (distance < minDistance) {
        minDistance = distance
        closestPoint = point
        closestParameter = t
        closestIndex = Math.round(t * (totalCount - 1))
      }
    }
    
    return {
      point: closestPoint,
      index: closestIndex,
      distance: minDistance,
      parameter: closestParameter
    }
  }
  
  /**
   * Get height at specific index
   */
  public getHeightAtIndex(index: number, totalCount: number, params: FieldParameters): number {
    const t = index / (totalCount - 1)
    return params.startHeight + (params.endHeight - params.startHeight) * t
  }
  
  /**
   * Get radius at specific index
   */
  public getRadiusAtIndex(index: number, totalCount: number, params: FieldParameters): number {
    const t = index / (totalCount - 1)
    return params.startRadius + (params.endRadius - params.startRadius) * t
  }
  
  /**
   * Check if point is inside spiral bounds
   */
  public isPointInside(point: Vector3, params: FieldParameters, totalCount: number): boolean {
    const maxRadius = Math.max(params.startRadius, params.endRadius)
    const horizontalDistance = Math.sqrt(point.x * point.x + point.z * point.z)
    
    return horizontalDistance <= maxRadius && 
           point.y >= params.startHeight && 
           point.y <= params.endHeight
  }
  
  /**
   * Get spiral bounds
   */
  public getSpiralBounds(params: FieldParameters): { 
    min: Vector3
    max: Vector3
    center: Vector3
  } {
    const maxRadius = Math.max(params.startRadius, params.endRadius)
    const minY = Math.min(params.startHeight, params.endHeight)
    const maxY = Math.max(params.startHeight, params.endHeight)
    
    return {
      min: new Vector3(-maxRadius, minY, -maxRadius),
      max: new Vector3(maxRadius, maxY, maxRadius),
      center: new Vector3(0, (minY + maxY) / 2, 0)
    }
  }
} 